import { Button, CheckboxSquare, Color, Radius, Spacing } from 'zens-storybook'
import React, { ForwardRefExoticComponent, PropsWithoutRef, ReactElement, RefAttributes, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps } from 'react-native'
import { ScheduleProgramData, ScheduleWeekday, ScheduleWeekdays } from 'zens-store'

import { TimeZoneConflict } from '../location'

import ScheduleSlider, { ScheduleSliderRef } from './slider'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  row: {
    alignItems: 'flex-start',
    flexDirection: 'row',
  },
  day: {
    width: 180,
  },
  slider: {
    marginLeft: Spacing.M,
  },
  skeletonSlider: {
    flex: 1,
    backgroundColor: Color.sleepingGrey,
    borderRadius: Radius.XS,
    minHeight: 36,
    margin: Spacing.XXS,
    marginRight: Spacing.L,
    marginBottom: 20,
  },
})

interface Props {
  companyId: number
  buildingId: number
  value?: ScheduleProgramData[]
  onChange?: (value?: ScheduleProgramData[]) => void
  color?: string
}

const ScheduleInput = forwardRef<ScheduleSliderRef, Props>((props, ref) => {
  const { companyId, buildingId, value, onChange, color } = props
  const { t } = useTranslation()

  const update = (data: ScheduleProgramData) => {
    const newValue = value?.filter((item) => item.weekday !== data?.weekday)
    newValue?.push(data)
    onChange?.(newValue)
  }

  const titleForDay = (day: ScheduleWeekday) => {
    switch (day) {
      case 'mon':
        return t('Monday')
      case 'tue':
        return t('Tuesday')
      case 'wed':
        return t('Wednesday')
      case 'thu':
        return t('Thursday')
      case 'fri':
        return t('Friday')
      case 'sat':
        return t('Saturday')
      case 'sun':
        return t('Sunday')
    }
  }

  return (
    <View style={styles.container}>
      <View style={styles.row} />
      {(value?.slice() || [])
        .sort((a, b) => ScheduleWeekdays.indexOf(a.weekday) - ScheduleWeekdays.indexOf(b.weekday))
        .map((data, index) => {
          return (
            <View key={data.weekday} style={[styles.row, { zIndex: value!.length - index * 100 }]}>
              <CheckboxSquare
                title={titleForDay(data.weekday)}
                style={styles.day}
                selected={data.is_enabled}
                onPress={() =>
                  update({
                    ...data,
                    is_enabled: !data.is_enabled,
                  })
                }
              />
              <ScheduleSlider
                ref={ref}
                style={styles.slider}
                disabled={!data.is_enabled}
                value={{ startAt: data.start_time, endAt: data.end_time }}
                onChange={(value) => {
                  update({
                    ...data,
                    start_time: value.startAt,
                    end_time: value.endAt,
                  })
                }}
                backgroundColor={color}
              />
            </View>
          )
        })}
      <TimeZoneConflict companyId={companyId} buildingId={buildingId} />
    </View>
  )
}) as ForwardRefExoticComponent<PropsWithoutRef<Props> & RefAttributes<ScheduleSliderRef>> & {
  Skeleton: (props: SkeletonProps) => ReactElement
}

interface SkeletonProps extends ViewProps {}

ScheduleInput.Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.row} />
      {ScheduleWeekdays.map((day, index) => (
        <View key={day} style={[styles.row, { zIndex: (ScheduleWeekdays.length - index) * 100 }]}>
          <Button.Skeleton style={styles.day} />
          <View style={[styles.slider, styles.skeletonSlider]} />
        </View>
      ))}
    </View>
  )
}

export default ScheduleInput
